/**
 * Rev-share affiliate-link URL rewriting (SFCC-11457).
 *
 * React-friendly port of SFRA's `tpaUrlHelper.js`. In SFRA the `.rev-share`
 * class is consumed by GTM, which appends affiliate tracking params on click.
 * On Workers we mirror that param-append in a small client listener that is
 * GATED by the Builder `gtmRevShare` flag (see RevShareLinkTracker): when GTM
 * is handling rev-share (`gtmRevShare === true`) the listener does not run; the
 * custom rewrite only fires when GTM is NOT configured (`gtmRevShare === false`).
 *
 * The affiliate params come from the client-readable `tpaBranding` cookie
 * (`afid|groupName|cid`, written server-side by `tpa.server.ts` / SFCC-11022),
 * with current-URL params taking precedence — same source order as SFRA's
 * `updateExternalURLTPAParams()`.
 *
 * These functions are pure / DOM-only so they unit-test cleanly under jsdom.
 */
import { getCookieValue } from "~/utils/cookies";

export const REV_SHARE_CLASS = "rev-share";
export const TPA_BRANDING_COOKIE = "tpaBranding";

export interface TpaBrandingParams {
  AFID: string;
  GroupName: string;
  CID: string;
}

/**
 * Reads a non-HttpOnly cookie value. Mirrors SFRA `getCookie`.
 * Accepts an explicit cookie string for testability (defaults to
 * `document.cookie` in the browser).
 */
export function getCookie(
  name: string,
  cookieString: string = typeof document !== "undefined" ? document.cookie : "",
): string | null {
  // Shared raw reader (slices after `name=`, so values containing "=" survive);
  // rev-share cookie values are URL-encoded, so decode here.
  const raw = getCookieValue(cookieString, name);
  return raw !== null ? decodeURIComponent(raw) : null;
}

/**
 * Resolves the affiliate params, preferring current-URL params over the
 * `tpaBranding` cookie (matches SFRA's `AFID = urlParam || cookie` order).
 * Returns null unless all three (AFID, GroupName, CID) are present.
 */
export function resolveTpaBrandingParams(
  search: string = typeof window !== "undefined" ? window.location.search : "",
  cookieString: string = typeof document !== "undefined" ? document.cookie : "",
): TpaBrandingParams | null {
  const urlParams = new URLSearchParams(search);
  const cookie = getCookie(TPA_BRANDING_COOKIE, cookieString);
  const [afidFromCookie, groupFromCookie, cidFromCookie] = cookie
    ? cookie.split("|")
    : [];

  const AFID = urlParams.get("AFID") || afidFromCookie || "";
  const GroupName = urlParams.get("GroupName") || groupFromCookie || "";
  const CID = urlParams.get("CID") || cidFromCookie || "";

  if (!AFID || !GroupName || !CID) return null;
  return { AFID, GroupName, CID };
}

/**
 * True when `url` resolves to a different origin than the current page.
 * Mirrors SFRA `isExternal`. Defensive against malformed/relative hrefs.
 */
export function isExternal(
  url: string,
  origin: string = typeof window !== "undefined" ? window.location.origin : "",
): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url, origin || undefined);
    // Only http(s) links get param injection — never corrupt mailto:/tel:/
    // javascript: or other custom schemes (their origin also differs).
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return false;
    }
    return parsed.origin !== origin;
  } catch {
    return false;
  }
}

/**
 * Appends the affiliate params to an absolute URL, returning the new href.
 * Mirrors SFRA `appendParameters` (idempotent — `set` overwrites duplicates).
 */
export function appendTpaParams(
  url: string,
  params: TpaBrandingParams,
): string {
  const urlObj = new URL(url);
  urlObj.searchParams.set("AFID", params.AFID);
  urlObj.searchParams.set("GroupName", params.GroupName);
  urlObj.searchParams.set("CID", params.CID);
  return urlObj.href;
}

/**
 * If `anchor` is an external `.rev-share` link and affiliate params are
 * available, rewrites its href in place and returns true. No-op (returns
 * false) otherwise. `set` makes re-clicks idempotent — no param buildup.
 */
export function applyRevShareParams(
  anchor: HTMLAnchorElement,
  params: TpaBrandingParams | null,
): boolean {
  if (!params) return false;
  const href = anchor.getAttribute("href");
  if (!href || !isExternal(anchor.href)) return false;
  anchor.href = appendTpaParams(anchor.href, params);
  return true;
}
